/* ==========================================================================
   1) <variant-radios> — handles variant changes
   Replaces your existing variant-radios definition (a tag can only be
   defined once, so remove the old one).
   ========================================================================== */
if (!customElements.get("variant-radios")) {
  customElements.define(
    "variant-radios",
    class VariantRadios extends HTMLElement {
      // Parts of the page refreshed after a variant change.
      // Adjust to your theme's selectors (price, gallery, SKU, buy button...).
      static SYNC_SELECTORS = [
        ".price",
        ".product-media",
        ".product-sku",
        ".product-form-buttons",
      ];

      connectedCallback() {
        this.onChange = this.onChange.bind(this);
        this.addEventListener("change", this.onChange);
      }

      disconnectedCallback() {
        this.removeEventListener("change", this.onChange);
        this.abortController?.abort();
      }

      get sectionId() {
        return this.dataset.section;
      }

      // NOTE: data-update-url is a true/false FLAG in this theme (it renders "true"),
      // so it must never be used as a URL. The product URL is data-url.
      get productUrl() {
        return this.dataset.url;
      }

      get shouldUpdateUrl() {
        return this.dataset.updateUrl !== "false" && !this.closest(".variant-picker-modal");
      }

      /**
       * Option names (e.g. "Dydis") for which a click must land on an IN-STOCK variant
       * whenever one exists for the clicked value, even if that means changing the
       * other options (e.g. Spalva). Comes from data-auto-switch-options in the Liquid.
       */
      get autoSwitchOptions() {
        return (this.dataset.autoSwitchOptions || "")
          .split(",")
          .map((name) => name.trim())
          .filter(Boolean);
      }

      get variantsMap() {
        try {
          const el = this.querySelector('script[data-id="product-variants-map"]');
          return el ? JSON.parse(el.textContent) : [];
        } catch (e) {
          console.error("Could not parse variants map", e);
          return [];
        }
      }

      /** Currently checked values, indexed by option position - 1. */
      getSelectedValues() {
        const selected = [];
        this.querySelectorAll("fieldset[data-option-position]").forEach((fieldset) => {
          const index = Number(fieldset.dataset.optionPosition) - 1;
          const checked = fieldset.querySelector("input[type=radio]:checked");
          selected[index] = checked ? checked.value : null;
        });
        return selected;
      }

      /**
       * Find the variant for a click.
       * - Normal options: keep the clicked value and pick the variant that matches the
       *   most of the other selected options (in-stock variants win ties).
       * - Auto-switch options (Dydis): keep the clicked value, but an IN-STOCK variant
       *   always wins over an out-of-stock one; among in-stock variants the one that
       *   matches the most other selected options (e.g. same Spalva) is chosen.
       */
      resolveVariant(changedIndex, changedValue, preferAvailable = false) {
        const selected = this.getSelectedValues();
        selected[changedIndex] = changedValue;

        const candidates = this.variantsMap.filter(
          (variant) => variant.options[changedIndex] === changedValue,
        );
        if (!candidates.length) return null;

        const scored = candidates.map((variant) => ({
          variant,
          score: variant.options.reduce(
            (sum, value, i) => (i !== changedIndex && value === selected[i] ? sum + 1 : sum),
            0,
          ),
        }));

        scored.sort((a, b) => {
          const availableDiff = Number(b.variant.available) - Number(a.variant.available);
          if (preferAvailable && availableDiff !== 0) return availableDiff;
          if (b.score !== a.score) return b.score - a.score;
          return availableDiff;
        });

        return scored[0].variant;
      }

      onChange(event) {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || target.type !== "radio") return;

        const fieldset = target.closest("fieldset[data-option-position]");
        if (!fieldset) return;

        const changedIndex = Number(fieldset.dataset.optionPosition) - 1;
        const preferAvailable = this.autoSwitchOptions.includes(fieldset.dataset.optionName);
        const variant = this.resolveVariant(changedIndex, target.value, preferAvailable);
        if (!variant) return;

        this.fetchAndRender(variant);
      }

      async fetchAndRender(variant) {
        // Cancel any in-flight request so an older response can't overwrite a newer one.
        this.abortController?.abort();
        this.abortController = new AbortController();

        const url =
          `${this.productUrl}?variant=${variant.id}` +
          `&section_id=${encodeURIComponent(this.sectionId)}`;

        this.classList.add("is-loading");

        try {
          const response = await fetch(url, { signal: this.abortController.signal });
          if (!response.ok) throw new Error(`Variant fetch failed: ${response.status}`);

          const html = new DOMParser().parseFromString(await response.text(), "text/html");

          const newPicker = html.querySelector(
            `variant-radios[data-section="${this.sectionId}"][data-product-id="${this.dataset.productId}"]`,
          );
          if (!newPicker) return;

          // Re-render the picker (checked state, disabled state, swatches).
          this.innerHTML = newPicker.innerHTML;

          this.updateFormVariantId(variant.id);
          if (this.shouldUpdateUrl) this.updateUrl(variant.id);
          this.syncParts(html);

          const options = {};
          this.querySelectorAll("input[type=radio]:checked").forEach((input) => {
            options[input.dataset.optionName] = input.value;
          });

          this.dispatchEvent(
            new CustomEvent("variant:change", {
              bubbles: true,
              detail: {
                variantId: variant.id,
                available: variant.available,
                options,
                sectionId: this.sectionId,
              },
            }),
          );
        } catch (error) {
          if (error.name !== "AbortError") console.error(error);
        } finally {
          this.classList.remove("is-loading");
        }
      }

      updateFormVariantId(variantId) {
        const scope =
          this.closest("product-info, .product, .variant-picker-modal, [id^=shopify-section]") ||
          document;
        scope.querySelectorAll('form[action*="/cart/add"] [name="id"]').forEach((input) => {
          input.value = variantId;
          input.disabled = false;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateUrl(variantId) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variantId);
        window.history.replaceState({}, "", url.toString());
      }

      syncParts(newDoc) {
        const oldRoot = this.closest(`#shopify-section-${this.sectionId}`) || document;
        const newRoot = newDoc.querySelector(`#shopify-section-${this.sectionId}`) || newDoc;

        VariantRadios.SYNC_SELECTORS.forEach((selector) => {
          const oldEls = oldRoot.querySelectorAll(selector);
          const newEls = newRoot.querySelectorAll(selector);
          oldEls.forEach((el, i) => {
            if (newEls[i]) el.innerHTML = newEls[i].innerHTML;
          });
        });
      }
    },
  );
}

/* ==========================================================================
   2) <product-form> — add to cart (hardened)
   ========================================================================== */
if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector("form");
        if (!this.form) return;

        const idInput = this.form.querySelector("[name=id]");
        if (idInput) idInput.disabled = false;

        this.onSubmitHandler = this.onSubmitHandler.bind(this);
        this.form.addEventListener("submit", this.onSubmitHandler);

        this.minicart = document.querySelector("mini-cart");
        this.eventContext = this.form.dataset.eventContext;
        this.hideErrors = this.dataset.hideErrors === "true";
      }

      disconnectedCallback() {
        this.form?.removeEventListener("submit", this.onSubmitHandler);
      }

      onSubmitHandler(event) {
        event.preventDefault();
        const submitButton = this.querySelector('[type="submit"]');
        if (!submitButton || submitButton.classList.contains("loading")) {
          return;
        }

        this.handleErrorMessage();

        submitButton.setAttribute("aria-disabled", true);
        submitButton.classList.add("loading");
        this.querySelector(".button-overlay-spinner")?.classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        formData.append(
          "sections",
          this.minicart.getSectionsToRender().map((section) => section.section),
        );
        formData.append("sections_url", window.location.pathname);
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);
              this.minicart.updateSectionContents();
              return;
            }

            this.minicart.renderContents(response);
            this.minicart.open();

            const modalRoot = document.querySelector(".variant-picker-modal");
            if (modalRoot && modalRoot.style.display === "block") {
              modalRoot.style.display = "none";
              modalRoot.innerHTML = "";
            }

            const event = new CustomEvent("cart:item-add", {
              detail: {
                items: [response],
                context: this.eventContext,
              },
            });
            document.dispatchEvent(event);

            publish(PUB_SUB_EVENTS.cartItemAdd, {
              source: this.eventContext,
              items: [response],
            });
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            submitButton.classList.remove("loading");
            submitButton.removeAttribute("aria-disabled");
            this.querySelector(".button-overlay-spinner")?.classList.add("hidden");
            if (typeof checkPreorderAndUpdateAttr === "function") {
              checkPreorderAndUpdateAttr();
            }
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) {
          return;
        }

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector(".product-form-error-message-wrapper");
        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(".product-form-error-message");

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    },
  );
}
